import {useState, useEffect} from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel, Stack,
    Typography, TextField, Chip, Box, Slider, Checkbox, FormControlLabel, RadioGroup, Radio, FormLabel, Divider } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

type FilterValue = string | number | [number, number] | string[] | boolean | null;
    interface FilterState {
      [columnId: string]: FilterValue;
    }
// convertire il formato data 
const parseEuropeanDateTime = (dateString: string) => {
    const parts = dateString.match(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})/);
    
    if (parts) {
        const year = parseInt(parts[3], 10);
        const month = parseInt(parts[2], 10) - 1; // Mese base 0
        const day = parseInt(parts[1], 10);
        const hour = parseInt(parts[4], 10);
        const minute = parseInt(parts[5], 10);
        const second = parseInt(parts[6], 10);
        
        return new Date(year, month, day, hour, minute, second);
    }
    return  new Date(dateString);
};

const getNestedValue = (obj: any, path: string) => {
    const keys = path.split('.');
    
    return keys.reduce((currentObj, key) => {
        return currentObj && currentObj[key] !== undefined ? currentObj[key] : undefined;
    }, obj);
};

export const applyAllFilters = (users: any[], filters: FilterState,) => {
    return users.filter(user => {
        for (const columnId in filters) {
            const filterValue = filters[columnId];

            if (filterValue === null || filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) {
                continue; 
            }

            switch (columnId) {
                case 'email':
                    if (typeof filterValue === 'string') {
                        if (!user.email.toLowerCase().includes(filterValue.toLowerCase())) {
                            return false;
                        }
                    }
                    break;
                 
                case 'tipo_account':
                    if (Array.isArray(filterValue) && filterValue.length > 0) {
                        const accountFilters = filterValue as string[];
                        if (!accountFilters.includes(user.tipo_account as string)) {
                            return false;
                        }
                    }
                    break;
                    
                case 'ultimoAccessoTimestamp':
                case 'statistiche_utente.ultimoAccessoTimestamp':
                case 'data_registrazione':
                    const userDateValue = getNestedValue(user, columnId);
                    
                    if (filterValue === 'online') {
                        if (userDateValue !== 'online') {
                            return false;
                        }
                    } else if (typeof filterValue === 'string') { 
                        const dates = filterValue.split(';');

                        const filterEndDateString = dates.length > 1 && dates[1] ? dates[1] : null;

                        if ((columnId === 'ultimoAccessoTimestamp' || columnId === 'statistiche_utente.ultimoAccessoTimestamp') && filterEndDateString) {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            const filterEndDateObj = new Date(filterEndDateString);
                            filterEndDateObj.setHours(0, 0, 0, 0);

                            if (filterEndDateObj.getTime() === today.getTime() && userDateValue === 'online') {
                                return true; 
                            }
                        }

                        if (userDateValue === 'online' || !userDateValue) return false;

                        const parsedUserDate = parseEuropeanDateTime(userDateValue);
                        let userAccessTime: number;

                        if (parsedUserDate && !isNaN(parsedUserDate.getTime())) {
                            userAccessTime = parsedUserDate.getTime();
                        } else {
                            return false;
                        }

                        const filterStartDate = dates[0] ? new Date(dates[0]).getTime() : 0;
                        const filterStart = isNaN(filterStartDate) ? 0 : filterStartDate;

                        const filterEndDateBase = filterEndDateString ? new Date(filterEndDateString).getTime() : Infinity;
                        const filterEnd = isNaN(filterEndDateBase) ? Infinity : filterEndDateBase + 86400000;

                        if (userAccessTime < filterStart || userAccessTime > filterEnd) {
                            return false;
                        }
                    }
                    break;

                case 'fileCaricati': 
                case 'fileCliente':
                case 'fileRielaborati':
                case 'clienti_condivisi_count': 
                case 'file_caricati_count':
                case 'xml_completati_count':  
                case 'pdf_scaricabili_count':
                case 'statistiche_utente.fileCaricati': 
                    if (Array.isArray(filterValue) && filterValue.length === 2) {
                        const min = Number(filterValue[0]);
                        const max = Number(filterValue[1]);
                        
                        const rawValue = getNestedValue(user, columnId);
                        const filesUploaded = Number(rawValue) || 0
                        if (isNaN(min) || isNaN(max) || isNaN(filesUploaded)) {
                            return false;
                        }
                        if (filesUploaded < min || filesUploaded > max) {
                            return false;
                        }
                    }
                    break;
            }
        }
        return true;
    });
};

// Aggiungi questo componente nello stesso file, fuori da FilterDialog

interface DateStatusFilterProps {
    columnId: string;
    currentValue: FilterValue;
    updateFilter: (columnId: string, value: FilterValue) => void;
    removeFilter: (columnId: string) => void;
    header: FilterableHeader; // Per accedere a label, ecc. se necessario
}

const DateStatusFilterControl: React.FC<DateStatusFilterProps> = ({ columnId, currentValue, updateFilter, removeFilter, header }) => {
    
    // Funzione helper per la data di oggi
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const today = getTodayDate();

    // Logica di inizializzazione
    let initialStart = '';
    let initialEnd = today;
    let initialSelection: 'online' | 'range' = 'range';

    if (currentValue === 'online') {
        initialSelection = 'online';
    } else if (typeof currentValue === 'string') {
        const dates = currentValue.split(';');
        if (dates.length >= 1) {
            // Estrai solo la parte YYYY-MM-DD
            initialStart = dates[0] ? dates[0].split('T')[0] : '';
            initialEnd = dates[1] ? dates[1].split('T')[0] : today;
            initialSelection = 'range';
        } 
    }
    
    const [dateRangeStart, setDateRangeStart] = useState<string>(initialStart);
    const [dateRangeEnd, setDateRangeEnd] = useState<string>(initialEnd);
    const [selection, setSelection] = useState<'online' | 'range'>(initialSelection);
    
    // useEffect per resettare lo stato interno quando il currentValue cambia dall'esterno (es. quando si cambia colonna selezionata nel Dialog)
    useEffect(() => {
        if (currentValue === 'online') {
            setSelection('online');
            setDateRangeStart('');
            setDateRangeEnd(today);
        } else if (typeof currentValue === 'string' && currentValue !== '') {
            const dates = currentValue.split(';');
            setDateRangeStart(dates[0] ? dates[0].split('T')[0] : '');
            setDateRangeEnd(dates[1] ? dates[1].split('T')[0] : today);
            setSelection('range');
        } else {
             // Caso in cui il filtro viene rimosso (null, '', [])
            setDateRangeStart('');
            setDateRangeEnd(today);
            setSelection('range');
        }
    }, [currentValue, today]);


    const handleDateRangeChange = (newStart: string, newEnd: string) => {
        const finalStart = newStart || '';
        const finalEnd = newEnd || getTodayDate(); // Assicurati di usare getTodayDate()

        setDateRangeStart(finalStart);
        setDateRangeEnd(finalEnd);

        // Applica il filtro range se almeno una data è specificata O se entrambe sono specificate.
        // Se sono entrambe vuote, è come non filtrare. La logica di rimozione deve essere gestita in base al caso.
        if (finalStart || finalEnd !== getTodayDate()) { // Applica se l'inizio è settato O se la fine è diversa da "oggi"
            updateFilter(columnId, `${finalStart};${finalEnd}`);
        } else {
            // Se sia l'inizio che la fine sono vuoti (o alla default di oggi), non c'è un filtro effettivo
            removeFilter(columnId);
        }
    };
    
    const handleRadioChange = (val: 'online' | 'range') => {
        setSelection(val);

        if (val === 'online') {
            updateFilter(columnId, 'online');
        } else if (val === 'range') {
            // Usa lo stato corrente aggiornato per l'aggiornamento (o i valori iniziali se non c'era filtro)
            handleDateRangeChange(dateRangeStart, dateRangeEnd); 
        }
    };

    return (
        <FormControl component="fieldset">
            <FormLabel component="legend">Filtra {header.label}</FormLabel>
            <RadioGroup
                value={selection}
                onChange={(e) => handleRadioChange(e.target.value as 'online' | 'range')}
            >
                { columnId === 'data_registrazione' ? <></> :
                    <FormControlLabel value="online" control={<Radio size="small" />} label="Solo Online" />
                }
                <FormControlLabel value="range" control={<Radio size="small" />} label="Filtra per Range Data" />
            </RadioGroup>
            
            {/* Input Data di Inizio */}
            <TextField
                label="Data di Inizio"
                type="date"
                value={dateRangeStart}
                disabled={selection === 'online'}
                onChange={(e) => handleDateRangeChange(e.target.value, dateRangeEnd)}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
            />
            {/* Input Data di Fine */}
            <TextField
                label="Data di Fine"
                type="date"
                value={dateRangeEnd}
                disabled={selection === 'online'}
                placeholder={today}
                onChange={(e) => handleDateRangeChange(dateRangeStart, e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
            />
        </FormControl>
    );
};

export type FilterableHeader = 
    | { label: string; id: string; type: 'text-suggest' }
    | { label: string; id: string; type: 'select-multi'; options: string[] }
    | { label: string; id: string; type: 'date-status' }
    | { label: string; id: string; type: 'range-number'; max: number };

export const FilterDialog = ({ tipo, open, onClose, currentFilters, onApplyFilters, onClearFilters } : {
    tipo:string;
    open: boolean;
    onClose: () => void;
    currentFilters: FilterState;
    onApplyFilters: (filters: FilterState) => void;
    onClearFilters: () => void;
}) => {
    let filterableHeaders : FilterableHeader[]
    const headers = () => {
      switch ( tipo ){
        case 'amministratore':
          return filterableHeaders = [
            { label: 'Email', id: 'email', type: 'text-suggest' },
            { label: 'Tipo Account', id: 'tipo_account', type: 'select-multi', options: ['amministratore', 'azienda', 'collaboratore', 'cliente'] },
            { label: 'Data di Registrazione', id: 'data_registrazione', type: 'date-status'},
            { label: 'Ultima Attività', id: 'ultimoAccessoTimestamp', type: 'date-status' },
            { label: 'Statistiche Uso', id: 'fileCaricati', type: 'range-number', max: 50 },
            { label: 'Statistiche Uso ( File Caricati )', id:'fileCliente', type: 'range-number', max: 50 },
            { label: 'File Rielaborati', id: 'fileRielaborati', type: 'range-number', max: 50 }, 
          ]
        case 'azienda':
          return filterableHeaders = [
            { label: 'Email', id: 'email', type: 'text-suggest' },
            { label: 'Ultima Attività', id: 'statistiche_utente.ultimoAccessoTimestamp', type: 'date-status' },
            { label: 'Statistiche Uso', id: 'statistiche_utente.fileCaricati', type: 'range-number', max: 50 },
          ]
        case 'collaboratore':
          return filterableHeaders = [
            { label: 'Email', id: 'email', type: 'text-suggest' },
            { label: 'Ultima Attività', id: 'statistiche_utente.ultimoAccessoTimestamp', type: 'date-status' },
            { label: 'Statistiche Uso', id: 'statistiche_utente.fileCaricati', type: 'range-number', max: 50 },
            { label: 'Clienti', id:'clienti_condivisi_count', type: 'range-number', max: 50 }, 
          ]
        case 'cliente':
          return filterableHeaders = [
            { label: 'Email', id: 'email', type: 'text-suggest' },
            { label: 'Ultima Attività', id: 'statistiche_utente.ultimoAccessoTimestamp', type: 'date-status' },
            { label: 'File caricati', id:'file_caricati_count', type: 'range-number', max: 50 },
            { label: 'XML completati', id:'xml_completati_count', type: 'range-number', max: 50 },
            { label: 'Scarica i PDF', id:'pdf_scaricabili_count', type: 'range-number', max: 50 },
          ]
        default : 
          return filterableHeaders = [
            { label: 'Email', id: 'email', type: 'text-suggest' },
            { label: 'Ultima Attività', id: 'statistiche_utente.ultimoAccessoTimestamp', type: 'date-status' },
            { label: 'Statistiche Uso', id: 'statistiche_utente.fileCaricati', type: 'range-number', max: 50 },
          ]
      }
    };

    // Stato locale per gestire i filtri in preparazione
    const [localFilters, setLocalFilters] = useState<FilterState>(currentFilters);
    // Stato per la colonna attualmente selezionata
    const [selectedColumnId, setSelectedColumnId] = useState<string>('email');

    useEffect(() => {
        // Sincronizza lo stato locale quando il dialogo si apre o i filtri esterni cambiano
        setLocalFilters(currentFilters);
    }, [open, currentFilters]);

    // Funzione per aggiornare un singolo filtro
    const updateFilter = (columnId: string, value: FilterValue) => {
        setLocalFilters(prev => ({
            ...prev,
            [columnId]: value,
        }));
    };
    
    // Funzione per rimuovere un singolo filtro
    const removeFilter = (columnId: string) => {
        const { [columnId]: removed, ...rest } = localFilters;
        setLocalFilters(rest);
    };

    // Componente di controllo specifico per la colonna
    const renderFilterControl = (header: FilterableHeader) => {
        const { id: columnId, type } = header;
        const currentValue = localFilters[columnId];
        
        switch (type) {
            case 'text-suggest': 
                return (
                    <TextField
                        label="Cerca Email"
                        value={currentValue as string || ''}
                        onChange={(e) => updateFilter(columnId, e.target.value)}
                        placeholder="Inserisci parte dell'email..."
                        fullWidth
                    />
                );

            case 'select-multi': 
                const options = header.type === 'select-multi' ? header.options : [];
                return (
                    <FormControl fullWidth>
                        <InputLabel>Tipo Account che vuoi visualizzare</InputLabel>
                        <Select
                            multiple
                            value={currentValue as string[] || []}
                            onChange={(e) => updateFilter(columnId, e.target.value as string[])}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {options?.map((option) => (
                                <MenuItem key={option} value={option}>
                                    <Checkbox checked={(currentValue as string[] || []).indexOf(option) > -1} />
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'date-status': 
                return (
                    <DateStatusFilterControl
                        columnId={columnId}
                        currentValue={currentValue}
                        updateFilter={updateFilter}
                        removeFilter={removeFilter}
                        header={header}
                    />
                );

            case 'range-number': 
                const rangeMax = header.max; 
                const [min, max] = currentValue as [number, number] || [0, rangeMax];

                return (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Typography gutterBottom>{`Inserisci un Range:`}</Typography>
                        <TextField 
                            label="Minimo" 
                            type="number" 
                            value={min} 
                            onChange={(e) => updateFilter(columnId, [parseInt(e.target.value), max])}
                            size="small"
                            sx={{ mt:2, mr: 2, width: '45%' }}
                        />
                         <TextField 
                            label="Massimo" 
                            type="number" 
                            value={max} 
                            onChange={(e) => updateFilter(columnId, [min, parseInt(e.target.value)])}
                            size="small"
                            sx={{ mt:2, width: '45%' }}
                        />
                    </Box>
                );

            default:
                return <Typography>Nessun controllo di filtro definito per questa colonna.</Typography>;
        }
    };
    
    const labelHeaders = headers();
    const selectedHeader = labelHeaders.find(h => h.id === selectedColumnId);
    
    return (
        <Dialog open={open} maxWidth="sm" fullWidth >
            <DialogTitle>
                <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Applica Filtri alla Tabella
            </DialogTitle>
            <DialogContent sx={{p:'2rem'}}>
                <Divider sx={{mb:'2.5rem'}}/>
                <Stack spacing={3} sx={{ p:'0 2rem 0 2rem'}}>
                    {/* Selezione Colonna */}
                    <FormControl fullWidth sx={{  mb:'1rem'}}>
                        <InputLabel id="select-column-label">Seleziona Colonna da Filtrare</InputLabel>
                        <Select
                            labelId="select-column-label"
                            value={selectedColumnId}
                            label="Seleziona Colonna da Filtrare"
                            onChange={(e) => setSelectedColumnId(e.target.value as string)}
                        >
                            {labelHeaders.map(h => (
                                <MenuItem key={h.id} value={h.id}>{h.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    {/* Controllo di Filtro Condizionale */}
                    <div style={{marginBottom:'1rem'}}>
                      {selectedHeader && renderFilterControl(selectedHeader)}
                    </div>

                   <Divider/>
                    
                    {/* Filtri Attivi */}
                    <Typography variant="subtitle1" sx={{display:'flex', justifyContent:'space-between'}}>
                        Filtri Attivi:
                        <Button onClick={onClearFilters} color='error' disabled={Object.keys(localFilters).length === 0} size='small'>Rimuovi Tutti</Button>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(localFilters).map(([id, value]) => {
                            if (value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
                                const header = filterableHeaders.find(h => h.id === id);
                                const label = header?.label || id;
                                const displayValue = Array.isArray(value) ? `${value[0]} - ${value[1]}` : String(value);

                                return (
                                    <Chip 
                                        key={id} 
                                        label={`${label}: ${displayValue}`} 
                                        onDelete={() => removeFilter(id)} 
                                        color="primary" 
                                        variant="outlined"
                                    />
                                );
                            }
                            return null;
                        })}
                        {Object.keys(localFilters).length === 0 && <Typography variant="body2" color="textSecondary">Nessun filtro applicato.</Typography>}
                    </Box>

                </Stack>
            </DialogContent>
            <DialogActions sx={{mb:'1.3rem'}} >
                  <Button onClick={onClose} variant="outlined" color='error' sx={{mr:'1rem'}}>Annulla</Button>
                  <Button 
                    onClick={() => onApplyFilters(localFilters)} 
                    variant="contained" 
                    startIcon={<FilterListIcon />}
                    sx={{mr:'1.7rem'}}
                  >
                    Applica Filtri
                  </Button>
            </DialogActions>
        </Dialog>
    );
};